import React, { createContext, useContext, useState, useEffect } from 'react';
import { GoogleAuthProvider, signInWithPopup, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase-config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Set the user immediately to allow basic auth-required features
          setUser(user);
          
          // Try to get admin status, but don't block on permission errors
          try {
            const userDoc = doc(db, 'users', user.uid);
            const userSnapshot = await getDoc(userDoc);
            setIsAdmin(userSnapshot.exists() && userSnapshot.data().role === 'admin');
          } catch (error) {
            // If we can't read the user doc, assume not an admin
            console.log('Not an admin user');
            setIsAdmin(false);
          }
        } else {
          setUser(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setUser(null);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [auth]);

  const signIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Create or update user document with basic info
      const userDoc = doc(db, 'users', result.user.uid);
      await setDoc(userDoc, {
        email: result.user.email,
        displayName: result.user.displayName,
        lastLogin: serverTimestamp(),
      }, { merge: true });

      console.log('Sign in successful:', {
        userId: result.user.uid,
        email: result.user.email
      });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Sign in error:', error);
      if (error.code === 'permission-denied') {
        throw new Error('This is the admin interface. Only administrators have access. Please use the regular interface if you are not an admin.');
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      console.log('Sign out successful');
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value = {
    user,
    signIn,
    signOut,
    isAdmin,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
